import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Tabs, Card, Text, Group, Stack, Switch, Button, Badge } from '@mantine/core';
import { IconCheck, IconX } from '@tabler/icons-react';
import { collection, doc, getDocs, query, setDoc, Timestamp, where, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';
import { useAvailabilities } from '../hooks/useAvailabilities';
import { useTranslation } from 'react-i18next';

function formatDT(date, locale) {
  try {
    const d = new Date(date);
    return new Intl.DateTimeFormat(locale || undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(d);
  } catch {
    return '';
  }
}

// events: array of FullCalendar events or normalized with { id, title, description, start, end, seriesId }
export default function AvailabilityModal({ planningId, user, events, opened, onClose }) {
  const { t, i18n } = useTranslation('common');
  const { map: availMap } = useAvailabilities(planningId, user?.uid);
  const [activeTab, setActiveTab] = useState('undeclared');
  const [confirm, setConfirm] = useState({ open: false, event: null, available: false });
  const [savingId, setSavingId] = useState(null);

  const normEvents = useMemo(() => {
    return (events || []).map((e) => ({
      id: e.id,
      title: e.title,
      description: e.extendedProps?.description || e.description || '',
      start: new Date(e.start),
      end: new Date(e.end || e.start),
      seriesId: e.extendedProps?.seriesId || e.seriesId || null,
    }));
  }, [events]);

  const declared = useMemo(() => {
    const declaredList = [];
    for (const ev of normEvents) {
      if (availMap.has(ev.id)) {
        const a = availMap.get(ev.id);
        declaredList.push({ ...ev, available: !!a.available });
      }
    }
    return declaredList;
  }, [normEvents, availMap]);

  const undeclared = useMemo(() => {
    const res = [];
    for (const ev of normEvents) {
      if (!availMap.has(ev.id)) res.push(ev);
    }
    return res;
  }, [normEvents, availMap]);

  useEffect(() => {
    // When opening, focus on undeclared if any
    if (opened) setActiveTab(undeclared.length ? 'undeclared' : 'declared');
  }, [opened, undeclared.length]);

  const persistSingle = async (ev, available) => {
    if (!planningId || !user?.uid) return;
    const id = `${ev.id}_${user.uid}`;
    const ref = doc(collection(db, 'plannings', planningId, 'availabilities'), id);
    await setDoc(ref, {
      eventId: ev.id,
      seriesId: ev.seriesId || null,
      userId: user.uid,
      available: !!available,
      updatedAt: Timestamp.now(),
    }, { merge: true });
  };

  const persistSeries = async (ev, available) => {
    if (!planningId || !user?.uid || !ev.seriesId) return;
    // Find all events in this planning with the same seriesId
    const eventsRef = collection(db, 'plannings', planningId, 'events');
    const q = query(eventsRef, where('seriesId', '==', ev.seriesId));
    const snap = await getDocs(q);
    const batch = writeBatch(db);
    const now = Timestamp.now();
    for (const d of snap.docs) {
      const eid = d.id;
      const ref = doc(collection(db, 'plannings', planningId, 'availabilities'), `${eid}_${user.uid}`);
      batch.set(ref, {
        eventId: eid,
        seriesId: ev.seriesId,
        userId: user.uid,
        available: !!available,
        updatedAt: now,
      }, { merge: true });
    }
    await batch.commit();
  };

  const onSave = async (ev, available) => {
    try {
      setSavingId(ev.id);
      if (!available && ev.seriesId) {
        // Ask apply to entire series or just this
        setConfirm({ open: true, event: ev, available });
      } else {
        await persistSingle(ev, available);
      }
    } finally {
      setSavingId(null);
    }
  };

  const confirmSeries = async (mode) => {
    const { event: ev, available } = confirm;
    if (!ev) return;
    setSavingId(ev.id);
    try {
      if (mode === 'series') {
        await persistSeries(ev, available);
      } else {
        await persistSingle(ev, available);
      }
    } finally {
      setSavingId(null);
      setConfirm({ open: false, event: null, available: false });
    }
  };

  const CardView = ({ ev, initialAvailable, declared }) => {
    const [val, setVal] = useState(initialAvailable ?? true); // default available
    useEffect(() => setVal(initialAvailable ?? true), [initialAvailable]);
    const hasDraft = declared ? val !== (initialAvailable ?? true) : true;

    return (
      <Card withBorder shadow="sm" radius="md" p="md">
        <Stack gap={6}>
          <Group justify="space-between" align="center">
            <Text fw={600} size="lg">{ev.title}</Text>
            <Group align="center" gap="xs">
              {declared && (
                <Badge color={val ? 'green' : 'red'} variant="light">{val ? t('availability.available') : t('availability.unavailable')}</Badge>
              )}
              <Switch
                checked={val}
                onChange={(e) => setVal(e.currentTarget.checked)}
                color="green"
                onLabel={<IconCheck size="1rem" stroke={3} style={{ color: 'white' }} />}
                offLabel={<IconX size="1rem" stroke={3} style={{ color: 'red' }} />}
              />
            </Group>
          </Group>
          {ev.description && <Text c="dimmed" size="sm">{ev.description}</Text>}
          <Text size="sm"><b>{t('availability.start')}:</b> {formatDT(ev.start, i18n.language)}</Text>
          <Text size="sm"><b>{t('availability.end')}:</b> {formatDT(ev.end, i18n.language)}</Text>
          <Group justify="flex-end" mt={4}>
            {(!declared || hasDraft) && (
              <Button size="xs" loading={savingId === ev.id} onClick={() => onSave(ev, val)}>
                {t('availability.save')}
              </Button>
            )}
          </Group>
        </Stack>
      </Card>
    );
  };

  return (
    <>
      <Modal opened={opened} onClose={onClose} title={t('availability.title')} size="lg">
        <Tabs value={activeTab} onChange={setActiveTab} keepMounted={false}>
          <Tabs.List>
            <Tabs.Tab value="undeclared">{t('availability.tabs.to_declare')} <Badge ml="xs" color="red" variant="filled">{undeclared.length}</Badge></Tabs.Tab>
            <Tabs.Tab value="declared">{t('availability.tabs.declared')} <Badge ml="xs" variant="light">{declared.length}</Badge></Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="undeclared" pt="md">
            <Stack>
              {undeclared.length === 0 ? (
                <Text size="sm" c="dimmed">{t('availability.empty_undeclared')}</Text>
              ) : (
                undeclared.map((ev) => (
                  <CardView key={ev.id} ev={ev} initialAvailable={true} declared={false} />
                ))
              )}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="declared" pt="md">
            <Stack>
              {declared.length === 0 ? (
                <Text size="sm" c="dimmed">{t('availability.empty_declared')}</Text>
              ) : (
                declared.map((ev) => (
                  <CardView key={ev.id} ev={ev} initialAvailable={!!ev.available} declared={true} />
                ))
              )}
            </Stack>
          </Tabs.Panel>
        </Tabs>
      </Modal>

      {/* Series confirmation modal for setting Unavailable */}
      <Modal
        opened={confirm.open}
        onClose={() => setConfirm({ open: false, event: null, available: false })}
        title={t('availability.series.title')}
      >
        <Stack>
          <Text>
            {t('availability.series.message')}
          </Text>
          <Group>
            <Button variant="default" onClick={() => confirmSeries('single')}>{t('availability.series.this_event')}</Button>
            <Button color="red" onClick={() => confirmSeries('series')}>{t('availability.series.entire_series')}</Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
